import { useState } from 'react';
import { BarChart3 } from 'lucide-react';
import {
  Button,
  Disclosure,
  EmptyState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { chartPalette } from './chartPalette';

export function ChartLegend({
  items,
}: {
  items: readonly { id: string; label: string; color?: 1 | 2 | 3 | 4 }[];
}) {
  return (
    <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-fg-muted" aria-label="Серии">
      {items.map((item, i) => (
        <li key={item.id} className="inline-flex items-center gap-2">
          <span
            aria-hidden
            className={`inline-block size-3 rounded-xs ${chartPalette[(item.color ?? i + 1) - 1]}`}
          />
          {item.label}
        </li>
      ))}
    </ul>
  );
}
export function ChartTooltipContent({
  title,
  rows,
}: {
  title: string;
  rows: readonly { label: string; value: string }[];
}) {
  return (
    <div className="space-y-2">
      <p className="font-semibold">{title}</p>
      <dl className="space-y-1">
        {rows.map((row, index) => (
          <div key={index} className="flex items-baseline justify-between gap-5">
            <dt className="text-fg-muted">{row.label}</dt>
            <dd className="font-medium whitespace-nowrap tabular-nums">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
export function ChartTable({
  label,
  headers,
  rows,
}: {
  label: string;
  headers: readonly string[];
  rows: readonly (readonly string[])[];
}) {
  const [open, setOpen] = useState(false),
    [count, setCount] = useState(50);
  return (
    <Disclosure
      title="Данные таблицей"
      variant="row"
      size="sm"
      open={open}
      onOpenChange={setOpen}
      contentSpacing="none"
    >
      {open && (
        <div className="pt-2 pb-3">
          <Table
            dense
            viewportProps={{ tabIndex: 0, 'aria-label': label + ' — прокрутка таблицы' }}
            viewportClassName="max-h-80 overflow-auto rounded-md border border-line focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-inset"
          >
            <caption className="sr-only">{label}</caption>
            <TableHeader>
              <TableRow>
                {headers.map((h, i) => (
                  <TableHead key={i} scope="col" className={i ? 'text-right' : ''}>
                    {h}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.slice(0, count).map((row, i) => (
                <TableRow key={i}>
                  {row.map((value, j) => (
                    <TableCell
                      key={j}
                      className={j ? 'text-right whitespace-nowrap tabular-nums' : 'whitespace-nowrap'}
                    >
                      {value}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {count < rows.length && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button size="sm" variant="secondary" onClick={() => setCount((n) => n + 50)}>
                Ещё {Math.min(50, rows.length - count)}
              </Button>
              <span className="text-xs text-fg-muted">
                Показано {Math.min(count, rows.length)} из {rows.length}
              </span>
            </div>
          )}
        </div>
      )}
    </Disclosure>
  );
}
export function ChartStatus({
  state,
  kind,
  error,
  onRetry,
  columns = 12,
  seriesCount = 2,
}: {
  state: 'loading' | 'empty' | 'error';
  kind: 'bar' | 'heat';
  columns?: number;
  seriesCount?: number;
  error?: string | null;
  onRetry?: () => void;
}) {
  if (state === 'loading') {
    const count = Math.max(1, columns);
    return (
      <div role="status" data-slot="chart-loading" aria-label="Загрузка графика" className="space-y-4">
        {kind === 'bar' ? (
          <>
            <div aria-hidden className="flex h-4 gap-5">
              {Array.from({ length: seriesCount }, (_, i) => (
                <Skeleton key={i} className="h-3 w-24" />
              ))}
            </div>
            <div aria-hidden className="flex min-w-0 gap-2 pt-4">
              <div className="flex h-56 w-14 shrink-0 flex-col justify-between">
                <Skeleton className="h-2 w-10" />
                <Skeleton className="h-2 w-10" />
                <Skeleton className="h-2 w-10" />
              </div>
              <div className="min-w-0 flex-1 overflow-x-auto pb-1">
                <div style={{ minWidth: count * 52 }}>
                  <div className="flex h-56 items-end border-b border-line">
                    {Array.from({ length: count }, (_, i) => (
                      <div key={i} className="flex flex-1 items-end justify-center gap-1 px-1">
                        {Array.from({ length: seriesCount }, (_, j) => (
                          <Skeleton
                            key={j}
                            className={`w-full ${['h-24', 'h-40', 'h-32', 'h-48'][(i + j) % 4]}`}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="flex h-8 items-center justify-around">
                    {Array.from({ length: count }, (_, i) => (
                      <Skeleton key={i} className="h-2 w-6" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div aria-hidden className="overflow-x-auto pb-2">
            <div className="w-max">
              <div className="mb-2 flex h-5 items-center gap-1">
                <span className="w-8" />
                <Skeleton className="h-3 w-20" />
              </div>
              {Array.from({ length: 7 }, (_, row) => (
                <div key={row} className="mb-1 flex items-center gap-1 last:mb-0">
                  <span className="w-8">
                    <Skeleton className="h-2 w-5" />
                  </span>
                  {Array.from({ length: count }, (_, i) => (
                    <Skeleton key={i} className="size-6" />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
        {kind === 'heat' && <Skeleton className="h-4 w-64" />}
        <div aria-hidden className="min-h-16 rounded-md bg-muted px-3 py-2">
          <Skeleton className="h-3 w-48" />
        </div>
        <Skeleton className="h-3 w-64" />
        <div aria-hidden className="flex h-10 items-center px-4">
          <Skeleton className="h-4 w-32" />
        </div>
        <span className="sr-only">Загружаем значения</span>
      </div>
    );
  }
  return (
    <EmptyState
      icon={<BarChart3 />}
      title={state === 'error' ? 'Не удалось показать график' : 'Пока нет данных'}
      description={
        state === 'error'
          ? (error ?? 'Не удалось получить значения. Попробуй ещё раз.')
          : 'Значения появятся здесь, когда они будут доступны.'
      }
      action={state === 'error' && onRetry ? { label: 'Повторить', onClick: onRetry } : undefined}
    />
  );
}
